"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { MouseEvent, ReactNode } from "react";
import { useResolvedCountry } from "@/providers/ResolvedCountryContext";
import { useServiceSolutionSlugs } from "@/providers/ServiceSolutionSlugsContext";
import { withCountryHref } from "@/util/seoCountryPaths";

type CmsLinkProps = {
  href?: string;
  target?: "_self" | "_blank";
  className?: string;
  role?: string;
  onMouseMove?: (event: MouseEvent<HTMLElement>) => void;
  children: ReactNode;
};

const isExternalHref = (href: string) =>
  href.startsWith("http://") ||
  href.startsWith("https://") ||
  href.startsWith("mailto:") ||
  href.startsWith("tel:");

const normalizePath = (path: string) => {
  if (!path) return "";
  const clean = path.split("?")[0] || "";
  if (!clean || clean === "/") return "/";
  return clean.replace(/\/+$/, "") || "/";
};

const splitHref = (href: string) => {
  const hashIndex = href.indexOf("#");
  if (hashIndex === -1) {
    return { path: href, hashId: "" };
  }

  const path = href.slice(0, hashIndex);
  const hashId = decodeURIComponent(
    href.slice(hashIndex + 1).split("?")[0] || ""
  ).trim();

  return { path, hashId };
};

const isSamePagePath = (currentPath: string, targetPath: string) => {
  // Hash-only href (#section) → always same page
  if (!targetPath || targetPath === "/") {
    return !targetPath;
  }
  const current = normalizePath(currentPath);
  const target = normalizePath(targetPath);
  return current === target || current.endsWith(target);
};

/** Smooth-scroll to an in-page hash (works with Next.js same-route Link clicks). */
export const scrollToHashTarget = (
  hashId: string,
  behavior: ScrollBehavior = "smooth"
) => {
  if (typeof window === "undefined" || !hashId) return;

  const hash = `#${hashId}`;
  if (window.location.hash !== hash) {
    window.history.pushState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${hash}`
    );
  }

  // pushState does not fire hashchange; notify ScrollToHash listeners.
  window.dispatchEvent(new Event("hashchange"));

  document
    .getElementById(hashId)
    ?.scrollIntoView({ behavior, block: "start" });
};

/**
 * CMS CTA / nav link:
 * - country-prefixed internal paths
 * - same-page hash smooth scroll (`#id` or `/slug#id`)
 * - external / mailto / tel / _blank
 */
const CmsLink = ({
  href,
  target,
  className,
  role,
  onMouseMove,
  children,
}: CmsLinkProps) => {
  const pathname = usePathname();
  const country = useResolvedCountry();
  const { serviceSlugs, solutionSlugs } = useServiceSolutionSlugs();
  const trimmed = href?.trim() || "#";
  // Services get city/country prefixes; solutions stay global.
  const resolvedHref = isExternalHref(trimmed)
    ? trimmed
    : withCountryHref(trimmed, country, { serviceSlugs, solutionSlugs });
  const resolvedTarget =
    target ?? (isExternalHref(resolvedHref) ? "_blank" : "_self");

  if (isExternalHref(resolvedHref) || resolvedTarget === "_blank") {
    return (
      <Link
        href={resolvedHref}
        target={resolvedTarget}
        rel={resolvedTarget === "_blank" ? "noopener noreferrer" : undefined}
        className={className}
        role={role}
        onMouseMove={onMouseMove}
      >
        {children}
      </Link>
    );
  }

  const { path, hashId } = splitHref(resolvedHref);
  const samePageHash = Boolean(hashId) && isSamePagePath(pathname, path);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!samePageHash) return;
    event.preventDefault();
    scrollToHashTarget(hashId);
  };

  return (
    <Link
      href={resolvedHref}
      className={className}
      role={role}
      target={resolvedTarget}
      scroll={!hashId}
      onClick={handleClick}
      onMouseMove={onMouseMove}
    >
      {children}
    </Link>
  );
};

export default CmsLink;
